import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { invitationsService } from '../api/members.service';
import { INVITATIONS_KEYS, MEMBERS_KEYS } from './queryKeys';
import type { CreateInvitationPayload } from '../types';

/**
 * Sends a new member invitation for the active organization.
 * Calls POST /api/tenants/invitations.
 *
 * @remarks
 * On success, invalidates **both** `MEMBERS_KEYS.byOrg` and `INVITATIONS_KEYS.byOrg`
 * because a new invitation affects both the members list (pending state) and any
 * separate invitations view that may be rendered concurrently.
 */
export function useInviteMember() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (payload: CreateInvitationPayload) => invitationsService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MEMBERS_KEYS.byOrg(orgId) });
      queryClient.invalidateQueries({ queryKey: INVITATIONS_KEYS.byOrg(orgId) });
    },
  });
}
